import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CloseButton } from 'components';
import { store } from 'store';

import { SaleCard } from './components/SaleCard/SaleCard';
import { DC_CLASSES, DC_DATA } from './consts';
import * as S from './Sale.styles';
import { SaleViewer } from './SaleViewer';

function isDesktop() {
  return window.innerWidth > 768;
}

export const Sale: FC = () => {
  const { resetMode } = store.commonStore;
  const [classIndex, setClassIndex] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [spinnerHidden, setSpinnerHidden] = useState([false, false, false]);
  const [viewer, setViewer] = useState<SaleViewer | null>(null);

  const containerRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([null, null, null]);

  // Touch swipe
  const touchStartX = useRef(0);
  const touchOnCanvas = useRef(false);

  // Fade in on mount
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Current class data
  const currentClass = DC_CLASSES[classIndex];

  // Cards to display
  const displayIndices = useMemo(() => {
    if (!currentClass) return [];
    if (isDesktop()) {
      return currentClass.indices.slice(0, 3);
    }
    return [mobileIndex];
  }, [currentClass, mobileIndex]);

  // Init 3D viewer on mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const containers = containerRefs.current.filter(Boolean) as HTMLDivElement[];
      const canvases = canvasRefs.current.filter(Boolean) as HTMLCanvasElement[];
      if (containers.length === 0 || canvases.length === 0) return;

      const v = new SaleViewer();
      const spinnerCbs = [0, 1, 2].map(
        (i) => (hidden: boolean) =>
          setSpinnerHidden((prev) => {
            const next = [...prev];
            next[i] = hidden;
            return next;
          })
      );
      v.createSlots(containers, canvases, spinnerCbs);
      v.startAnimation();
      setViewer(v);
    });

    return () => cancelAnimationFrame(raf);
  }, []);

  // Destroy viewer on unmount
  useEffect(() => {
    return () => {
      if (viewer) {
        viewer.destroy();
      }
    };
  }, [viewer]);

  // Update slot visibility when navigation changes
  useEffect(() => {
    if (!viewer) return;
    for (let i = 0; i < 3; i++) {
      viewer.setSlotVisible(i, displayIndices[i] !== undefined);
    }
  }, [viewer, displayIndices]);

  // Load models when viewer is ready or navigation changes
  useEffect(() => {
    if (!viewer) return;

    if (isDesktop()) {
      const cls = DC_CLASSES[classIndex];
      if (!cls) return;
      cls.indices.forEach((dcIdx, i) => {
        if (i >= 3) return;
        const slot = viewer.getSlot(i);
        if (slot) viewer.loadModelIntoSlot(slot, dcIdx, DC_DATA);
      });
    } else {
      const slot = viewer.getSlot(0);
      if (slot) viewer.loadModelIntoSlot(slot, mobileIndex, DC_DATA);
    }
  }, [viewer, classIndex, mobileIndex]);

  // Resize handler
  useEffect(() => {
    if (!viewer) return;
    const onResize = () => viewer.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [viewer]);

  // Navigation
  const navigate = useCallback(
    (dir: number) => {
      if (isDesktop()) {
        const newClassIdx = (classIndex + dir + DC_CLASSES.length) % DC_CLASSES.length;
        setClassIndex(newClassIdx);
        setMobileIndex(DC_CLASSES[newClassIdx].indices[0]);
      } else {
        const newIdx = (mobileIndex + dir + DC_DATA.length) % DC_DATA.length;
        setMobileIndex(newIdx);
        for (let c = 0; c < DC_CLASSES.length; c++) {
          if (DC_CLASSES[c].indices.includes(newIdx)) {
            setClassIndex(c);
            break;
          }
        }
      }
    },
    [classIndex, mobileIndex]
  );

  // Keyboard navigation
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigate(-1);
      else if (e.key === 'ArrowRight') navigate(1);
      else if (e.key === 'Escape') resetMode();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navigate, resetMode]);

  // Dot click
  const handleDotClick = useCallback((idx: number) => {
    if (isDesktop()) {
      setClassIndex(idx);
      setMobileIndex(DC_CLASSES[idx].indices[0]);
    } else {
      setMobileIndex(idx);
      for (let c = 0; c < DC_CLASSES.length; c++) {
        if (DC_CLASSES[c].indices.includes(idx)) {
          setClassIndex(c);
          break;
        }
      }
    }
  }, []);

  // Touch swipe (exclude canvas for orbit controls)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchOnCanvas.current = !!(e.target as HTMLElement).closest('canvas');
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchOnCanvas.current) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(dx) > 50) navigate(dx > 0 ? -1 : 1);
    },
    [navigate]
  );

  // Buy click
  const handleBuyClick = useCallback((dcIndex: number) => {
    const dc = DC_DATA[dcIndex];
    if (dc.status === 'out-of-stock') return;
    alert('Purchase: ' + dc.name + ' for ' + dc.price + ' \u039E\nComing soon!');
  }, []);

  // Dots: desktop = class dots, mobile = all items
  const dots = isDesktop()
    ? DC_CLASSES.map((_, i) => ({ idx: i, active: i === classIndex }))
    : DC_DATA.map((_, i) => ({ idx: i, active: i === mobileIndex }));

  return (
    <S.Overlay $visible={visible} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <CloseButton onClick={resetMode} />
      <S.Container>
        <S.Title>INFRASTRUCTURE MODULES</S.Title>
        <S.ClassRow>
          Data Center Class: <S.ClassValue>{currentClass?.cls}</S.ClassValue>
        </S.ClassRow>

        <S.CardsWrap>
          <S.Nav onClick={() => navigate(-1)}>{'\u276E'}</S.Nav>
          <S.Cards>
            {[0, 1, 2].map((slotIdx) => {
              const dcIndex = displayIndices[slotIdx];
              const isHidden = dcIndex === undefined;

              return (
                <S.Card key={slotIdx} $hidden={isHidden}>
                  <SaleCard
                    dc={isHidden ? null : DC_DATA[dcIndex]}
                    spinnerHidden={spinnerHidden[slotIdx]}
                    onBuyClick={() => !isHidden && handleBuyClick(dcIndex!)}
                    containerRef={(el) => {
                      containerRefs.current[slotIdx] = el;
                    }}
                    canvasRef={(el) => {
                      canvasRefs.current[slotIdx] = el;
                    }}
                  />
                </S.Card>
              );
            })}
          </S.Cards>
          <S.Nav onClick={() => navigate(1)}>{'\u276F'}</S.Nav>
        </S.CardsWrap>

        <S.Dots>
          {dots.map((dot) => (
            <S.Dot key={dot.idx} $active={dot.active} onClick={() => handleDotClick(dot.idx)} />
          ))}
        </S.Dots>
      </S.Container>
    </S.Overlay>
  );
};
